import { useCallback, useMemo, useRef } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'

import useToggle from 'hooks/useToggle'

import DropdownChevron from 'LuxKit/Dropdown/DropdownChevron'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import Heading from 'LuxKit/Typography/Heading'
import RadioInput from 'LuxKit/Radio/RadioInput'

import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'

export const DEPARTURE_OPTIONS: Array<App.SearchDeparturePort> = [
  {
    searchType: 'departurePort',
    value: 'le_d3d9446802a44259755d38e6d163e820',
    format: {
      mainText: 'Australia',
    },
  },
  {
    searchType: 'departurePort',
    value: 'le_9fc3d7152ba9336a670e36d0ed79bc43',
    format: {
      mainText: 'New Zealand',
    },
  },
  {
    searchType: 'departurePort',
    value: 'le_cf874aad79e14b401a4c86954a596fa5',
    format: {
      mainText: 'North America',
    },
  },
  {
    searchType: 'departurePort',
    value: 'le_fdbf7de11c1a054dc927adf66d9b5fce',
    format: {
      mainText: 'Asia',
    },
  },
  {
    searchType: 'departurePort',
    value: 'le_12767faf1bf8cafc7594430586c558c2',
    format: {
      mainText: 'Europe',
    },
  },
  {
    searchType: 'departurePort',
    value: 'le_93db85ed909c13838ff95ccfa94cebd9',
    format: {
      mainText: 'Mediterranean',
    },
  },
]

interface Props {
  departure: App.SearchDeparturePort
  onChange: (newDeparture: App.SearchDeparturePort) => void
}

function CruiseDepartureFromDropdown(props: Props) {
  const { onChange, departure } = props
  const intl = useIntl()

  const localizedLabels: Record<string, string> = useMemo(() => ({
    Australia: intl.formatMessage({ id: 'G1SQmRdSAlOTHcWNkhzd', defaultMessage: 'Australia', description: 'Cruise departure region label for Australia in departure dropdown' }),
    'New Zealand': intl.formatMessage({ id: 'Uo5wnr-C1dAjWmOtoexZ', defaultMessage: 'New Zealand', description: 'Cruise departure region label for New Zealand in departure dropdown' }),
    'North America': intl.formatMessage({ id: 'HGK4OLNLG8sAvA7Ddqes', defaultMessage: 'North America', description: 'Cruise departure region label for North America in departure dropdown' }),
    Asia: intl.formatMessage({ id: 'qP95ZjW0pH8_7MyNfsna', defaultMessage: 'Asia', description: 'Cruise departure region label for Asia in departure dropdown' }),
    Europe: intl.formatMessage({ id: 'WKbn8eTA8FN1IJDCMxXC', defaultMessage: 'Europe', description: 'Cruise departure region label for Europe in departure dropdown' }),
    Mediterranean: intl.formatMessage({ id: 'eZMDKguiHTTdMZx6kkqY', defaultMessage: 'Mediterranean', description: 'Cruise departure region label for Mediterranean in departure dropdown' }),
  }), [intl])

  const [open, toggleSortOpen, , closeDropdown] = useToggle()
  const triggerRef = useRef<HTMLDivElement>(null)

  const onChangeSelection = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedDeparture = DEPARTURE_OPTIONS.find(option => option.value === e.currentTarget.value)
    if (selectedDeparture) {
      onChange(selectedDeparture)
      closeDropdown()
    }
  }, [closeDropdown, onChange])

  const selectedLabel = useMemo(() => {
    const mainText = DEPARTURE_OPTIONS.find(option => option.value === departure.value)?.format.mainText
    return mainText ? (localizedLabels[mainText] ?? mainText) : undefined
  }, [departure, localizedLabels])

  return <>
    <Heading variant="heading2" as="button" onClick={toggleSortOpen} ref={triggerRef}>
      <i>{selectedLabel}</i>
      <DropdownChevron open={open} />
    </Heading>

    <DropdownList
      size="S"
      anchorRef={triggerRef}
      triggerRef={triggerRef}
      open={open}
      onClose={closeDropdown}
    >
      <Sheet padding={16}>
        <Group direction="vertical" gap={16}>
          {DEPARTURE_OPTIONS.map(option => <RadioInput
            value={option.value}
            onChange={onChangeSelection}
            key={option.value}
            checked={option.value ? option.value === departure.value : !departure}
            name="sort"
          >
            {localizedLabels[option.format.mainText] ?? option.format.mainText}
          </RadioInput>)}
        </Group>
      </Sheet>
    </DropdownList>
  </>
}

export default React.memo(CruiseDepartureFromDropdown)
